import { AgentProfile, AppPresence, GuestProfile } from "@closerplatform/spinner-openapi";
import { CallReason, roomEvents } from "@closerplatform/closer-sdk";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { IUserService } from "@fidget/common/services/userService";
import { logger } from "@fidget/common/utils";
import { getTimeString } from "@fidget/common/utils/timeFormatter";
import { UserPresenter } from "@fidget/common/utils/userPresenter";
import { MessageTag } from "@fidget/common/models/messageTag";
import { isGuestProfile, UserProfile } from "@fidget/common/models/userProfile";
import {
  CallEndedPayload,
  ChatItemStatus,
  EmptyChatItem,
  ContactEnquiryChatItem,
  IChatItem,
  MeetingChatItem,
  MeetingSchedulerChatItem,
  NoteChatItem,
  NotificationChatItem,
  StatusChangeChatItem,
  UserTextChatItem,
  CallProposalChatItem,
  CobrowsingProposalChatItem,
  CobrowsingStartedPayload,
  CobrowsingEndedPayload,
  MessageWithFormChatItem,
  FormPayload
} from "@fidget/common/models/chat/chatItems";
import RoomEvent = roomEvents.RoomEvent;
import { MeetingStatus } from "@fidget/common/models/meeting-status";

import { IForeSpinnerService } from "../../services/spinnerService";
import { hardcodedCloserBotProfile } from "@fidget/common/models/hacks";

interface IEventConverter<T> {
  toChatItem(event: T): Promise<IChatItem>;
}

const MINUTE_IN_MILLIS = 60000;

abstract class AbstractEventConverter {
  protected constructor(protected readonly userService: IUserService) { }

  protected getUser(userId?: string): UserProfile {
    let user: UserProfile | undefined = userId ? this.userService.getUser(userId) : undefined;
    if (!user) {
      logger.error(`Unknown user id = ${userId}`);
      const profile: AgentProfile = {
        id: "unknown",
        firstName: "Unknown",
        lastName: "User",
        presence: AppPresence.Unavailable,
        email: "",
        isBot: false,
        isActive: true,
        orgRole: "unknown",
        tagGroups: [],
        tags: [],
        groups: []
      };

      return profile;
    }
    return user;
  }

  getIntlParams = (assignee: string, requesterId?: string): { [key: string]: string | number; } => {
    const name = UserPresenter.from(
      this.getUser(assignee),
      assignee === this.userService.getCurrentUser().id
    ).displayedName;
    const users = [name];
    if (requesterId) {
      const requester = UserPresenter.from(
        this.getUser(requesterId),
        requesterId === this.userService.getCurrentUser().id
      ).displayedName;
      users.push(requester);
    }
    return users.reduce((result, user, i) => ({ ...result, [`user${i + 1}`]: user }), {
      count: users.length
    });
  };
}

export type ChatItemStatusCallback = (itemId: string, itemTimestamp: number) => ChatItemStatus;

export class ArtichokeEventConverter
  extends AbstractEventConverter
  implements IEventConverter<RoomEvent>
{
  private readonly spinnerService: IForeSpinnerService;
  private readonly getChatItemStatus: ChatItemStatusCallback;

  constructor(userService: IUserService, spinnerService: IForeSpinnerService) {
    super(userService);

    this.spinnerService = spinnerService;
    this.getChatItemStatus = () => ChatItemStatus.SENT;
  }

  getGuestEvent(ev: roomEvents.CustomMessageSent): IChatItem {
    switch (ev.subtag) {
      case MessageTag.CALL_ANSWERED: {
        const time = getTimeString((ev.context as any).timestamp);
        return new NotificationChatItem(
          ev.messageId,
          ev.timestamp,
          intl.formatMessage("conversation.event.callStarted", { time })
        );
      }
      case MessageTag.CALL_ENDED: {
        const payload = ev.context as CallEndedPayload;
        const durationInMinutes = Math.ceil(payload.duration / MINUTE_IN_MILLIS);
        switch (payload.reason) {
          case CallReason.TimeLimitExceeded:
          case CallReason.Hangup: {
            return payload.duration
              ? new NotificationChatItem(
                ev.messageId,
                ev.timestamp,
                intl.formatMessage("conversation.event.callEnded", { durationInMinutes })
              )
              : new NotificationChatItem(
                ev.messageId,
                ev.timestamp,
                intl.formatMessage("conversation.event.callCancelled")
              );
          }
          case CallReason.Timeout: {
            return new NotificationChatItem(
              ev.messageId,
              ev.timestamp,
              intl.formatMessage("conversation.event.callCancelled")
            );
          }
          case CallReason.CallRejected: {
            return new NotificationChatItem(
              ev.messageId,
              ev.timestamp,
              intl.formatMessage("conversation.event.callDeclined")
            );
          }
          default: {
            return new NotificationChatItem(ev.messageId, ev.timestamp, ev.message);
          }
        }
      }
      case MessageTag.CALL_UNSUCCESSFUL: {
        return new NotificationChatItem(
          ev.messageId,
          ev.timestamp,
          intl.formatMessage("conversation.event.callUnsuccessful.widget")
        );
      }

      case MessageTag.COBROWSING_SESSION_STARTED: {
        const time = getTimeString((ev.context as CobrowsingStartedPayload).timestamp);
        return new NotificationChatItem(
          ev.messageId,
          ev.timestamp,
          intl.formatMessage("conversation.event.cobrowsingStarted", { time })
        );
      }

      case MessageTag.COBROWSING_SESSION_ENDED: {
        const durationInMinutes: number = Math.ceil(
          (ev.context as CobrowsingEndedPayload).duration / MINUTE_IN_MILLIS
        );
        return new NotificationChatItem(
          ev.messageId,
          ev.timestamp,
          intl.formatMessage("conversation.event.cobrowsingEnded", { durationInMinutes })
        );
      }
      default: {
        return new NotificationChatItem(ev.messageId, ev.timestamp, ev.message);
      }
    }
  }

  // TODO: IMHO this is dead code since for advisers methods from /agent/models/room/chat/ChatItem.ts are used
  getAdviserEvent(ev: roomEvents.CustomMessageSent): IChatItem {
    switch (ev.subtag) {
      case MessageTag.CALL_ANSWERED: {
        const time = getTimeString((ev.context as any).timestamp);
        return new NotificationChatItem(
          ev.messageId,
          ev.timestamp,
          intl.formatMessage("conversation.event.callReceived", { time })
        );
      }
      case MessageTag.CALL_ENDED: {
        const payload = ev.context as CallEndedPayload;
        const durationInMinutes = Math.ceil(payload.duration / MINUTE_IN_MILLIS);
        const time = getTimeString(ev.timestamp);
        switch (payload.reason) {
          case CallReason.Hangup: {
            return payload.duration
              ? new NotificationChatItem(
                ev.messageId,
                ev.timestamp,
                intl.formatMessage("conversation.event.callEnded", { durationInMinutes })
              )
              : new NotificationChatItem(
                ev.messageId,
                ev.timestamp,
                intl.formatMessage("conversation.event.missedCall", { time })
              );
          }
          case CallReason.Timeout: {
            return new NotificationChatItem(
              ev.messageId,
              ev.timestamp,
              intl.formatMessage("conversation.event.missedCall", { time })
            );
          }
          case CallReason.CallRejected: {
            const user = this.getUser(payload.userId);
            const name = UserPresenter.from(
              user,
              user.id === this.userService.getCurrentUser().id
            ).displayedName;
            return new NotificationChatItem(
              ev.messageId,
              ev.timestamp,
              intl.formatMessage("conversation.event.callRejected", { time, name })
            );
          }
          default: {
            return new NotificationChatItem(ev.messageId, ev.timestamp, ev.message);
          }
        }
      }
      // TODO: This is *probably* not needed here
      case MessageTag.CALL_UNSUCCESSFUL: {
        return new NotificationChatItem(
          ev.messageId,
          ev.timestamp,
          "Call unsuccessful for adviser"
        );
      }
      default: {
        return new NotificationChatItem(ev.messageId, ev.timestamp, ev.message);
      }
    }
  }

  async toChatItem(ev: roomEvents.RoomEvent): Promise<IChatItem> {
    if (roomEvents.MessageSent.isMessageSent(ev)) {
      const user = this.getUser(ev.authorId);
      return new UserTextChatItem(
        ev.messageId,
        ev.timestamp,
        ev.message,
        user,
        this.getChatItemStatus(ev.messageId, ev.timestamp),
        undefined,
        ev.context
      );
    } else if (roomEvents.CustomMessageSent.isCustomMessageSent(ev)) {
      switch (ev.subtag) {
        case MessageTag.CALL_ANSWERED:
        case MessageTag.CALL_ENDED:
        case MessageTag.COBROWSING_SESSION_ENDED:
        case MessageTag.COBROWSING_SESSION_STARTED:
          return isGuestProfile(this.userService.getCurrentUser())
            ? this.getGuestEvent(ev)
            : this.getAdviserEvent(ev);
        case MessageTag.CALL_UNSUCCESSFUL:
          return isGuestProfile(this.userService.getCurrentUser())
            ? this.getGuestEvent(ev)
            : this.getAdviserEvent(ev);
        case MessageTag.CONTACT_ENQUIRY: {
          const user = this.userService.getBotProfile() || this.userService.getAgent(ev.authorId);
          const recipient = this.userService.getCurrentUser() as GuestProfile;
          const shouldShowForm = recipient.email === undefined && recipient.phone === undefined;
          if (!shouldShowForm) {
            return new EmptyChatItem(`${ev.authorId}-${ev.roomId}-${ev.timestamp}`, ev.timestamp);
          }
          return new ContactEnquiryChatItem(
            ev.messageId,
            ev.timestamp,
            ev.context.agreement,
            true,
            user
          );
        }
        case MessageTag.CONTACT_ENQUIRY_WITHOUT_GDPR: {
          const user = this.userService.getBotProfile() || this.userService.getAgent(ev.authorId);
          const recipient = this.userService.getCurrentUser() as GuestProfile;
          const shouldShowForm = recipient.email === undefined && recipient.phone === undefined;
          if (!shouldShowForm) {
            return new EmptyChatItem(`${ev.authorId}-${ev.roomId}-${ev.timestamp}`, ev.timestamp);
          }
          return new ContactEnquiryChatItem(
            ev.messageId,
            ev.timestamp,
            ev.context.agreement,
            false,
            user
          );
        }
        // not sure about hacking it, agent has better solution, maybe it's time for big refactor. Above one is also buggy
        case MessageTag.LEKTA_MESSAGE: {
          const user = this.userService.getBotProfile() || this.userService.getAgent(ev.authorId);

          const textMessage = ev.context.message || ev.message;
          if (!textMessage) {
            return new EmptyChatItem(`${ev.authorId}-${ev.roomId}-${ev.timestamp}`, ev.timestamp);
          }

          const isCloserBot = Object.keys(ev.context || {}).length === 0;

          return new UserTextChatItem(
            ev.messageId,
            ev.timestamp,
            textMessage,
            {
              ...user,
              ...(isCloserBot ? hardcodedCloserBotProfile : {})
            },
            this.getChatItemStatus(ev.messageId, ev.timestamp),
            ev.context.attachments,
            ev.context
          );
        }
        case MessageTag.MESSAGE_WITH_ATTACHMENTS: {
          const user = this.getUser(ev.authorId);
          return new UserTextChatItem(
            ev.messageId,
            ev.timestamp,
            ev.context.message || ev.message,
            user,
            this.getChatItemStatus(ev.messageId, ev.timestamp),
            ev.context.attachments
          );
        }
        case MessageTag.CALL_PROPOSAL: {
          const user = this.getUser(ev.authorId);
          return new CallProposalChatItem(
            ev.messageId,
            ev.timestamp,
            ev.message,
            user,
            this.getChatItemStatus(ev.messageId, ev.timestamp)
          );
        }
        case MessageTag.COBROWSING_PROPOSAL: {
          const user = this.getUser(ev.authorId);
          return new CobrowsingProposalChatItem(
            ev.messageId,
            ev.timestamp,
            ev.message,
            user,
            this.getChatItemStatus(ev.messageId, ev.timestamp)
          );
        }
        case MessageTag.COBROWSING_ACCEPTATION: {
          const user = this.getUser(ev.authorId);
          return new UserTextChatItem(
            ev.messageId,
            ev.timestamp,
            ev.message,
            user,
            this.getChatItemStatus(ev.messageId, ev.timestamp)
          );
        }
        case MessageTag.ASSIGNEE_CHANGED: {
          const { assignee, requesterId } = ev.context;
          return new NotificationChatItem(
            ev.messageId,
            ev.timestamp,
            intl.formatMessage(
              "conversation.event.assigneeChanged",
              this.getIntlParams(assignee, requesterId)
            )
          );
        }
        case MessageTag.MEETING_SCHEDULED:
        case MessageTag.MEETING_RESCHEDULED:
        case MessageTag.MEETING_CANCELLED: {
          const meetingStatus =
            ev.subtag === MessageTag.MEETING_RESCHEDULED
              ? MeetingStatus.RESCHEDULED
              : ev.subtag === MessageTag.MEETING_CANCELLED
                ? MeetingStatus.CANCELLED
                : MeetingStatus.SCHEDULED;
          return new MeetingChatItem(
            ev.messageId,
            ev.timestamp,
            ev.context.start,
            ev.context.duration,
            this.userService.getAgent(ev.context.agent),
            this.userService.getBotProfile(),
            meetingStatus,
            false // TODO: waiting for CLSR-2982
          );
        }
        case MessageTag.MEETING: {
          const user = this.userService.getBotProfile();
          const { token, status, meetingId } = ev.context;
          return new MeetingSchedulerChatItem(
            ev.messageId,
            ev.timestamp,
            status,
            token,
            user,
            meetingId
          );
        }
        case MessageTag.ASSIGNEE_REMOVED: {
          const { assignee } = ev.context;
          const name = UserPresenter.from(
            this.getUser(assignee),
            assignee === this.userService.getCurrentUser().id
          ).displayedName;
          return new NotificationChatItem(
            ev.messageId,
            ev.timestamp,
            intl.formatMessage("conversation.event.assigneeRemoved", { name })
          );
        }
        case MessageTag.NOTE: {
          const { body, user } = ev.context;
          return new NoteChatItem(ev.messageId, ev.timestamp, body, this.getUser(user));
        }
        case MessageTag.STATUS_CHANGED: {
          const { status, agent } = ev.context;
          const agentProfile = (agent && this.getUser(agent)) || undefined;
          return new StatusChangeChatItem(ev.messageId, ev.timestamp, status, agentProfile);
        }
        case MessageTag.FOLLOWER_ADDED: {
          const { user, requesterId } = ev.context;
          if (user === requesterId) {
            const name = UserPresenter.from(
              this.getUser(user),
              user === this.userService.getCurrentUser().id
            ).displayedName;
            return new NotificationChatItem(
              ev.messageId,
              ev.timestamp,
              intl.formatMessage("conversation.event.startedFollowing", { name })
            );
          }
          return new NotificationChatItem(
            ev.messageId,
            ev.timestamp,
            intl.formatMessage(
              "conversation.event.followerAdded",
              this.getIntlParams(ev.context.user, requesterId)
            )
          );
        }
        case MessageTag.FILE_DOWNLOADED:
        case MessageTag.LINK_CLICKED:
        case MessageTag.COBROWSING_ENDED:
        case MessageTag.ALL_FOLLOWERS_REMOVED:
          return new EmptyChatItem(`${ev.authorId}-${ev.roomId}-${ev.timestamp}`, ev.timestamp);

        case MessageTag.FOLLOWER_REMOVED: {
          const { user, requesterId } = ev.context;
          if (user === requesterId) {
            const name = UserPresenter.from(
              this.getUser(user),
              user === this.userService.getCurrentUser().id
            ).displayedName;
            return new NotificationChatItem(
              ev.messageId,
              ev.timestamp,
              intl.formatMessage("conversation.event.stoppedFollowing", { name })
            );
          }
          return new NotificationChatItem(
            ev.messageId,
            ev.timestamp,
            intl.formatMessage(
              "conversation.event.followerRemoved",
              this.getIntlParams(ev.context.user, requesterId)
            )
          );
        }
        case MessageTag.MESSAGE_WITH_FORM: {
          const { formId }: FormPayload = ev.context;
          const user = this.getUser(ev.authorId);
          const submission = await this.spinnerService.getWidgetForm(formId);

          const isCloserBot = submission.name !== "ankieta_MAX";

          return new MessageWithFormChatItem(
            ev.messageId,
            ev.timestamp,
            ev.message,
            {
              ...user,
              ...(isCloserBot ? hardcodedCloserBotProfile : {})
            },
            this.getChatItemStatus(ev.messageId, ev.timestamp),
            formId,
            submission
          );
        }
        default: {
          return new NotificationChatItem(ev.messageId, ev.timestamp, ev.message);
        }
      }
    }

    logger.warn(`unsupported RoomEvent: ${ev.tag}`);
    return new EmptyChatItem(`${ev.authorId}-${ev.roomId}-${ev.timestamp}`, ev.timestamp);
  }
}
